/**
 * Persists in-progress upload-form metadata across an unexpected reload,
 * tied to the specific file it was typed for.
 *
 * iOS WebKit can reload the tab outright under memory pressure mid-upload
 * (see T434086), wiping any typed metadata. Saving a snapshot lets us
 * restore it - but only for the SAME file: if the user reloads and picks
 * a different (e.g. smaller) file, the old title/description/categories/
 * license/Wikidata item/trim range should not silently carry over onto
 * unrelated footage. We fingerprint the file by name+size+lastModified
 * (cheap, no hashing) and only apply the draft when a freshly picked file
 * matches.
 */
import type { UploadForm } from "./types"

const STORAGE_KEY = "c2c_form_draft"

interface FileFingerprint {
  name: string
  size: number
  lastModified: number
}

interface FormDraft {
  file: FileFingerprint
  form: Partial<UploadForm>
}

function fingerprint(file: File): FileFingerprint {
  return { name: file.name, size: file.size, lastModified: file.lastModified }
}

function sameFile(a: FileFingerprint, b: FileFingerprint): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified
}

/** Save the current form, tagged with the file it belongs to. Debounce
 *  this at the call site - it is not debounced here. */
export function saveDraft(file: File, form: UploadForm): void {
  try {
    const draft: FormDraft = { file: fingerprint(file), form }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch {
    // sessionStorage unavailable - losing the draft is not fatal
  }
}

/** Return the saved form fields, but ONLY if `file` matches the file the
 *  draft was saved for. Returns null otherwise (including "no draft"). */
export function loadDraftForFile(file: File): Partial<UploadForm> | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const draft = JSON.parse(stored) as FormDraft
    if (!draft?.file || !sameFile(draft.file, fingerprint(file))) return null
    return draft.form
  } catch {
    return null
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
